/**
 * MeetingModeLabel: 企業名の右に表示する対応形式タグ（「オンライン」「対面」）。
 * design-mockups/*.html準拠（BR-U6-12関連、2026-08-19デザイン更新で新規追加）。
 * `meetingModePolicy`から静的に導出できるため、追加のAPI呼び出しは不要。
 */

import { createElement } from "../core/shadowDomUtil";
import {
  MEETING_LABEL_ONLINE_COLOR,
  MEETING_LABEL_ONLINE_BG,
  MEETING_LABEL_ONLINE_BORDER,
  MEETING_LABEL_ONSITE_COLOR,
  MEETING_LABEL_ONSITE_BG,
  MEETING_LABEL_ONSITE_BORDER,
} from "./designTokens";

export type MeetingModePolicy = "ONLINE_ONLY" | "ON_SITE_ONLY" | "HYBRID" | null | undefined;

/**
 * 共通CSS（呼び出し元のCSS文字列に連結して使用する）。
 * 商談形式だけを色で区別する。青=オンライン、緑=対面としてこのタグ内に用途を限定する。
 */
export const MEETING_MODE_LABEL_STYLES = `
.spica-meeting-mode-label { display: inline-flex; align-items: center; gap: 4px; margin-left: 6px; vertical-align: middle; }
.spica-meeting-mode-label__tag {
  display: inline-flex; align-items: center; padding: 1px 5px; border: 1px solid;
  border-radius: 3px; font-size: 11px; font-weight: 700; line-height: 1.4;
}
.spica-meeting-mode-label__tag.is-online {
  color: ${MEETING_LABEL_ONLINE_COLOR}; background: ${MEETING_LABEL_ONLINE_BG}; border-color: ${MEETING_LABEL_ONLINE_BORDER};
}
.spica-meeting-mode-label__tag.is-onsite {
  color: ${MEETING_LABEL_ONSITE_COLOR}; background: ${MEETING_LABEL_ONSITE_BG}; border-color: ${MEETING_LABEL_ONSITE_BORDER};
}
`;

/**
 * `meetingModePolicy`に応じた対応形式ラベル要素を構築する。
 * ONLINE_ONLY=「オンライン商談」のみ、ON_SITE_ONLY=「対面」のみ、
 * HYBRID=「オンライン商談」「対面」の両方、
 * null/undefined（displayMode=HIDDEN時等）の場合はnullを返す（呼び出し元は追加しない）。
 */
export function renderMeetingModeLabel(meetingModePolicy: MeetingModePolicy): HTMLElement | null {
  if (!meetingModePolicy) return null;

  const showOnline = meetingModePolicy === "ONLINE_ONLY" || meetingModePolicy === "HYBRID";
  const showOnsite = meetingModePolicy === "ON_SITE_ONLY" || meetingModePolicy === "HYBRID";

  // 「オンライン」単独だと商談形式ではなくオンライン状態の表示に誤認され、対面希望の
  // リクエストと齟齬が生じうるため、商談形式であることを明示して「オンライン商談」と表示する。
  // タグtextとaria-labelの表記ゆれを防ぐため文言は定数へ集約する（オンライン/対面で対称）。
  const ONLINE_TEXT = "オンライン商談";
  const ONSITE_TEXT = "対面";

  const ariaParts: string[] = [];
  if (showOnline) ariaParts.push(ONLINE_TEXT);
  if (showOnsite) ariaParts.push(ONSITE_TEXT);

  const label = createElement("span", {
    className: "spica-meeting-mode-label",
    attrs: { "aria-label": `対応形式: ${ariaParts.join("・")}` },
  });

  if (showOnline) {
    label.appendChild(
      createElement("span", { className: "spica-meeting-mode-label__tag is-online", text: ONLINE_TEXT })
    );
  }
  if (showOnsite) {
    label.appendChild(
      createElement("span", { className: "spica-meeting-mode-label__tag is-onsite", text: ONSITE_TEXT })
    );
  }

  return label;
}
